import Embed from 'quill/blots/embed';
import Inline from 'quill/blots/inline';
import {
  booleanFieldContentSchema,
  identityContentSchema,
  type Field,
  fieldSchema,
  FieldType,
  BooleanField,
} from './types';
import Quill from 'quill/core';
import { renderIdentity } from './renderers';
import { renderDate } from './renderers';

class FieldBlot extends Embed {
  static tagName = 'div';
  static create(field: Field) {
    const node = super.create() as HTMLSpanElement;

    node.setAttribute('data-field-name', field.name);
    node.setAttribute('data-field-id', field.id);
    node.setAttribute('id', `field-${field.id}-${crypto.randomUUID()}`);

    let className = 'field';
    if (!field.value?.value) {
      className += ' empty';
    }
    node.setAttribute('class', className);

    node.textContent = (field.value?.value as string) || field.name;

    return node;
  }

  static value(node: HTMLSpanElement): Omit<Field, 'value'> | null {
    const name = node.getAttribute('data-field-name');
    const id = node.getAttribute('data-field-id');

    if (!name) return null;

    return {
      type: this.blotName as FieldType,
      name,
      id: id as string,
    };
  }
}

export class TextFieldBlot extends FieldBlot {
  static blotName = FieldType.VARIABLE_TEXT;
}

export class MultipleChoiceFieldBlot extends FieldBlot {
  static blotName = FieldType.MULTIPLE_CHOICE;
}

export class DateFieldBlot extends FieldBlot {
  static blotName = FieldType.DATE;

  static create(field: Field) {
    const node = super.create(field);

    node.textContent = field.value?.value ? renderDate(field.value.value as Date) : field.name;

    return node;
  }
}

export class IdentityFieldBlot extends FieldBlot {
  static blotName = FieldType.IDENTITY;

  static schema = identityContentSchema;

  static create(field: Field) {
    const node = super.create(field);

    if (field.value?.value) {
      const parsed = this.schema.parse(field.value.content);
      node.textContent = renderIdentity(parsed);
    } else {
      node.textContent = field.name;
    }

    return node;
  }
}

export class BooleanFieldBlot extends Inline {
  static blotName = FieldType.BOOLEAN;
  static tagName = 'div';

  static schema = booleanFieldContentSchema;

  static create(field: BooleanField) {
    const node = super.create();
    node.setAttribute('class', 'variable');
    node.setAttribute('data-field-name', field.name);
    node.setAttribute('data-field-id', field.id);
    node.setAttribute('id', `field-${field.id}`);
    if (field.reverse) {
      node.setAttribute('data-reversed', field.reverse.toString());
    }
    if (field.value?.value) {
      this.schema.parse(field.value);
    }

    return node;
  }

  static formats(node: HTMLSpanElement): Field {
    const name = node.getAttribute('data-field-name');
    const id = node.getAttribute('data-field-id');
    const value = node.getAttribute('data-field-value');
    const reverse = node.getAttribute('data-reversed');
    return fieldSchema.parse({
      type: FieldType.BOOLEAN,
      name,
      id,
      value: value === 'true',
      reverse: reverse === 'true',
    });
  }
}

export const register = () => {
  Quill.register({
    [`formats/${FieldType.VARIABLE_TEXT}`]: TextFieldBlot,
    [`formats/${FieldType.DATE}`]: DateFieldBlot,
    [`formats/${FieldType.BOOLEAN}`]: BooleanFieldBlot,
    [`formats/${FieldType.IDENTITY}`]: IdentityFieldBlot,
    [`formats/${FieldType.MULTIPLE_CHOICE}`]: MultipleChoiceFieldBlot,
  });
};
